import classNames from 'clsx';
import flow from 'lodash/flow';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { api } from 'src/client/api';
import { stores } from 'src/client/stores';
import { openLinkWithAlexaDialog } from 'src/common/actions/dialog';
import {
  ADD_DEVICE_BUTTON,
  ADD_DEVICE_LINE_2,
  DELETE_DEVICE_ERROR,
  HEADLINE,
  LOAD_ERROR,
} from 'src/common/constants/localizations/devices';
import { withReactHooks } from 'src/common/decorators/withReactHooks';
import { useStores } from 'src/common/providers';
import debounce from 'src/common/utils/debounce';
import { LocationAndLocalizationContext } from '../../../providers/LocationAndLocalizationProvider';
import AlexaLinkButton from '../../shared/button/AlexaLinkButton';
import PillButton from '../../shared/button/PillButton';
import AddDeviceDialog from './AddDeviceDialog';
import AddDeviceSuccessDialog from './AddDeviceSuccessDialog';
import CannotDeleteDeviceDialog from './CannotDeleteDeviceDialog';
import DeleteDeviceDialog from './DeleteDeviceDialog';
import DeviceList from './DeviceList';
import css from './device-settings.module.scss';

// exported for testing
export class DeviceSettings extends Component {
  static propTypes = {
    devices: PropTypes.array.isRequired,
    failedToLoad: PropTypes.bool,
    tuneInUserSerial: PropTypes.string.isRequired,
    doNotAskToDeleteDevice: PropTypes.bool.isRequired,
    actions: PropTypes.shape({
      openLinkWithAlexaDialog: PropTypes.func.isRequired,
    }).isRequired,
  };

  static contextType = LocationAndLocalizationContext;

  state = {
    isAddDeviceDialogOpen: false,
    isSuccessAddDeviceDialogOpen: false,
    isDeleteDeviceDialogOpen: false,
    isCannotDeleteDeviceDialogOpen: false,
    showDeleteError: false,
    deviceToDelete: null,
  };

  handleDeleteError = () => {
    this.setState({
      showDeleteError: true,
    });
  };

  handleDeleteDevice = async (device) => {
    const { doNotAskToDeleteDevice, tuneInUserSerial } = this.props;

    if (device.serial === tuneInUserSerial) {
      this.setState({
        isCannotDeleteDeviceDialogOpen: true,
      });

      return;
    }

    if (!doNotAskToDeleteDevice) {
      this.setState({
        isDeleteDeviceDialogOpen: true,
        deviceToDelete: device,
      });

      return;
    }

    try {
      await api.user.deleteDevice(device.id);
      stores.userStore.getState().removeDevice(device.id);
    } catch {
      this.handleDeleteError();
    }

    this.setState({
      deviceToDelete: null,
    });
  };

  closeCannotDeleteDeviceDialogHandler = () => {
    this.setState({
      isCannotDeleteDeviceDialogOpen: false,
    });
  };

  closeDeleteDeviceDialogHandler = () => {
    this.setState({
      isDeleteDeviceDialogOpen: false,
      showDeleteError: false,
      deviceToDelete: null,
    });
  };

  openAddDeviceDialogHandler = () => {
    this.setState({
      isAddDeviceDialogOpen: true,
    });
  };

  closeAddDeviceDialogHandler = () => {
    this.setState({
      isAddDeviceDialogOpen: false,
    });
  };

  openSuccessAddDeviceHandler = () => {
    this.setState({
      isAddDeviceDialogOpen: false,
      isSuccessAddDeviceDialogOpen: true,
    });
  };

  closeSuccessAddDeviceHandler = () => {
    this.setState({
      isSuccessAddDeviceDialogOpen: false,
    });
  };

  render() {
    const { devices, actions } = this.props;
    const { getLocalizedText } = this.context;
    const {
      isAddDeviceDialogOpen,
      isSuccessAddDeviceDialogOpen,
      isCannotDeleteDeviceDialogOpen,
      isDeleteDeviceDialogOpen,
      deviceToDelete,
      showDeleteError,
    } = this.state;

    if (!devices.length) {
      return (
        <div className="row">
          <p className={classNames('col-xs-12 col-sm-8', css.headline)}>
            {getLocalizedText(LOAD_ERROR)}
          </p>
        </div>
      );
    }

    return (
      <div>
        <div className="row">
          <div className="col-xs-12">
            <p className={css.deviceHeadlineWrapper}>
              <span className={css.addDeviceText} data-testid="addDeviceLine2">
                {getLocalizedText(ADD_DEVICE_LINE_2)}
              </span>
              <span data-testid="headlineText" className={css.headline}>
                {getLocalizedText(HEADLINE)}:
              </span>
            </p>
          </div>
        </div>
        {showDeleteError && (
          <div className="row">
            <div className="col-xs-12">
              <p data-testid="mainError" className={css.errorField}>
                {getLocalizedText(DELETE_DEVICE_ERROR)}
              </p>
            </div>
          </div>
        )}
        <div className="row">
          <div className="col-xs-12">
            <DeviceList devices={devices} onDeleteDevice={this.handleDeleteDevice} />
          </div>
          <div className="col-xs-12 col-sm-4">
            <AlexaLinkButton
              className={css.addAlexaButton}
              onClick={actions.openLinkWithAlexaDialog}
            />
            <PillButton
              id="addDevice"
              dataTestId="addDevice"
              className={css.addDeviceButton}
              onClick={debounce(this.openAddDeviceDialogHandler)}
              label={getLocalizedText(ADD_DEVICE_BUTTON)}
            />
          </div>
        </div>
        <AddDeviceDialog
          key="addDeviceDialog"
          shouldShow={isAddDeviceDialogOpen}
          handleClose={this.closeAddDeviceDialogHandler}
          successCallback={this.openSuccessAddDeviceHandler}
        />
        <AddDeviceSuccessDialog
          key="addDeviceSuccessDialog"
          shouldShow={isSuccessAddDeviceDialogOpen}
          handleClose={this.closeSuccessAddDeviceHandler}
        />
        <CannotDeleteDeviceDialog
          key="cannotDeleteDeviceDialog"
          shouldShow={isCannotDeleteDeviceDialogOpen}
          handleClose={this.closeCannotDeleteDeviceDialogHandler}
        />
        <DeleteDeviceDialog
          key="deleteDeviceDialog"
          shouldShow={isDeleteDeviceDialogOpen}
          handleClose={this.closeDeleteDeviceDialogHandler}
          handleDeleteError={this.handleDeleteError}
          deviceId={deviceToDelete?.id || null}
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  const { app } = state;

  return {
    tuneInUserSerial: app.tuneInUserSerial,
    doNotAskToDeleteDevice: app.doNotAskToDeleteDevice,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        openLinkWithAlexaDialog,
      },
      dispatch,
    ),
  };
}

export default flow(
  connect(mapStateToProps, mapDispatchToProps),
  withReactHooks(() => {
    const { userStore } = useStores();

    return {
      devices: userStore.devices(),
    };
  }),
)(DeviceSettings);
